import { useMemo } from "react";
import type { NewNarrativeData } from "../types/narrative";

export function useNarrativeStructure(
  narrativeData: NewNarrativeData | undefined,
  selectedSections?: Set<string>,
) {
  // Build visibleSectionIds: always include intro and table_of_contents, plus selected sections
  const visibleSectionIds = useMemo(() => {
    const ids = new Set<string>(["intro", "table_of_contents"]);
    if (selectedSections) {
      selectedSections.forEach((id) => ids.add(id));
    }
    return ids;
  }, [selectedSections]);

  // Filter sections based on visibleSectionIds
  const visibleSections = useMemo(() => {
    if (!narrativeData) return [];
    return narrativeData.sections.filter((section) =>
      visibleSectionIds.has(section.id),
    );
  }, [narrativeData, visibleSectionIds]);

  // Calculate total groups and build section mapping
  const { totalGroups, sectionMap } = useMemo(() => {
    let groupCount = 0;
    const map: Array<{
      sectionId: string;
      startGroupIndex: number;
      groupCount: number;
    }> = [];

    visibleSections.forEach((section) => {
      map.push({
        sectionId: section.id,
        startGroupIndex: groupCount,
        groupCount: section.groups.length,
      });
      groupCount += section.groups.length;
    });

    return { totalGroups: groupCount, sectionMap: map };
  }, [visibleSections]);

  // Helper to get section ID for a given group index
  const getSectionInfoForGroup = (groupIndex: number) => {
    return sectionMap.find(
      (s) =>
        groupIndex >= s.startGroupIndex &&
        groupIndex < s.startGroupIndex + s.groupCount,
    );
  };

  return {
    visibleSectionIds,
    visibleSections,
    totalGroups,
    sectionMap,
    getSectionInfoForGroup,
  };
}
